import { useEffect, useRef } from 'react';
import { PopupDataMap, PopupName, usePopups } from '@hooks/popups/usePopups';
import { runSafely } from '@utils/promise';

const LOG_PREFIX = '[AiTools.Popups]';
const LOG_LOCATION = 'usePopupHostApi';

/**
 * The payload always comes from the host page, so anything that is not a plain
 * object means "no payload". The cast is unchecked by design: the host page is
 * trusted to the same degree the popup's own props are, and every field of a
 * popup detail type is optional precisely so a malformed payload degrades to
 * defaults.
 */
const toDetail = (value: unknown): PopupDataMap[PopupName] =>
  (value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : {}) as PopupDataMap[PopupName];

type PopupTransition = 'open' | 'close';

type PopupListener = () => void;

type PopupListeners = Record<
  PopupTransition,
  Partial<Record<PopupName, Set<PopupListener>>>
>;

type PopupOpenState = Partial<Record<PopupName, boolean>>;

type UsePopupHostApiOptions = {
  /** Popups this root renders. */
  names: PopupName[];
};

/**
 * The React side of `AiTools.Popups` — how the host page (edusson) opens,
 * closes and observes the popups rendered by `Popups`:
 *
 *   AiTools.Popups.openPopup('SubscriptionPopup');
 *   AiTools.Popups.openPopup('SubscriptionPopup', { variant: 'detection_paywall' });
 *   AiTools.Popups.closePopup('SubscriptionPopup');
 *   AiTools.Popups.onOpenPopup('SubscriptionPopup', callback);
 *   AiTools.Popups.onClosePopup('SubscriptionPopup', callback);
 *
 * The host object is loaded before the bundle and queues every call until this
 * hook connects, so a call made before `Popups` is mounted is replayed rather
 * than lost.
 */
export const usePopupHostApi = ({ names }: UsePopupHostApiOptions) => {
  const { popups, openPopup, closePopup } = usePopups();
  const listenersRef = useRef<PopupListeners>({ open: {}, close: {} });
  const wasOpenRef = useRef<PopupOpenState | null>(null);
  const namesRef = useRef(names);

  // The effects below read the names through a ref, so a caller that builds a
  // new array on every render does not reconnect the host on each of them.
  useEffect(() => {
    namesRef.current = names;
  }, [names]);

  useEffect(() => {
    const hostPopups = window.AiTools?.Popups;

    if (!hostPopups) {
      return undefined;
    }

    const isKnownPopup = (method: string, name: string): name is PopupName => {
      const isKnown = namesRef.current.includes(name as PopupName);

      if (!isKnown) {
        console.error(`${LOG_PREFIX} ${method}: unknown popup "${name}"`);
      }

      return isKnown;
    };

    const addListener = (
      transition: PopupTransition,
      method: string,
      name: string,
      callback: unknown
    ) => {
      if (!isKnownPopup(method, name)) {
        return;
      }

      if (typeof callback !== 'function') {
        console.error(
          `${LOG_PREFIX} ${method}: the callback is not a function`
        );
        return;
      }

      const listeners = listenersRef.current[transition];

      listeners[name] ??= new Set();
      listeners[name]?.add(callback as PopupListener);
    };

    const handler: AiToolsPopupsHandler = {
      openPopup: (name, data) => {
        if (isKnownPopup('openPopup', name)) {
          openPopup(name, toDetail(data));
        }
      },
      closePopup: (name) => {
        if (isKnownPopup('closePopup', name)) {
          closePopup(name);
        }
      },
      onOpenPopup: (name, callback) =>
        addListener('open', 'onOpenPopup', name, callback),
      onClosePopup: (name, callback) =>
        addListener('close', 'onClosePopup', name, callback),
    };

    // One React root per page serves the host: with the SPA and the island
    // bundle on one page, two of them would open two dialogs for a single call.
    if (!hostPopups.connect(handler)) {
      console.error(
        `${LOG_PREFIX} another React root already serves this page, this one stays passive`
      );
      return undefined;
    }

    return () => hostPopups.disconnect(handler);
  }, [closePopup, openPopup]);

  // Listeners follow the store, not the host calls, so a popup opened or closed
  // from inside React (`usePopups()`, the X, the backdrop) notifies the host as
  // well — its follow-up offers chain off the close.
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    const isOpen: PopupOpenState = {};

    namesRef.current.forEach((name) => {
      isOpen[name] = Boolean(popups[name]?.isOpen);
    });

    wasOpenRef.current = isOpen;

    // The first run only records the baseline.
    if (!wasOpen) {
      return;
    }

    namesRef.current.forEach((name) => {
      if (isOpen[name] === wasOpen[name]) {
        return;
      }

      const transition: PopupTransition = isOpen[name] ? 'open' : 'close';

      listenersRef.current[transition][name]?.forEach((listener) =>
        runSafely(listener, { location: LOG_LOCATION, popup: name, transition })
      );
    });
  }, [popups]);
};
